/**
 * @file DashboardConfigurator.js
 * @version 5.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-02
 * @modified 2026-05-29
 *
 * Dashboard layout and widget placement configurator
 * with grid-based spanning preview and drag-and-drop.
 * Supports embedded mode (rendered inside UserProfile panel) or standalone Modal mode.
 * Accepts an optional `user` prop; when present, `adminOnly: true` widgets
 * are filtered out of the available-widgets list for non-admin users.
 */

import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import './DashboardConfigurator.css';
import {
  widgetRegistry,
  layoutPresets,
  defaultPlacements,
  canPlaceWidget,
  columnWidthOptions,
  resolveLayout,
  isWidgetVisibleTo
} from '../widgets';
import { badgeOf } from '../utils/permissions';
import { useSlideOutButton } from './SlideOutButton';

const DEFAULT_CUSTOM_COLUMNS = [
  { type: 'medium', width: '1fr' },
  { type: 'medium', width: '1fr' },
  { type: 'medium', width: '1fr' }
];

const DashboardConfigurator = forwardRef(function DashboardConfigurator({
  isOpen,
  onClose,
  currentSettings,
  onSave,
  onReset,
  saving,
  embedded = false,
  user = null
}, ref) {
  const isAdmin = !!user?.is_admin;
  const viewer = { badge: badgeOf(user), isAdmin };
  useSlideOutButton();

  const [layoutPreset, setLayoutPreset] = useState(currentSettings.layout_preset);
  const [widgetPlacement, setWidgetPlacement] = useState(currentSettings.widget_placement);
  const [errors, setErrors] = useState([]);
  const [customColumns, setCustomColumns] = useState(
    currentSettings.widget_placement?.custom_columns || DEFAULT_CUSTOM_COLUMNS
  );
  const [widgetSpans, setWidgetSpans] = useState({});
  const [spanDirections, setSpanDirections] = useState({});
  const [widgetCollapsed, setWidgetCollapsed] = useState({});
  const [widgetRowSpans, setWidgetRowSpans] = useState({});
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const buttons = document.querySelectorAll('.btn-slideout');
      buttons.forEach(button => {
        const label = button.querySelector('.btn-label');
        if (!label) return;
        const badge = button.querySelector('.btn-badge');
        const labelWidth = label.offsetWidth;
        const badgeSpace = badge ? 24 : 0;
        const expandedWidth = 45 + labelWidth + badgeSpace + 12;
        button.style.setProperty('--expanded-width', `${expandedWidth}px`);
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [widgetPlacement, draggedWidget]);

  useEffect(() => {
    if (isOpen || embedded) {
      setLayoutPreset(currentSettings.layout_preset);
      setWidgetPlacement(currentSettings.widget_placement);
      setCustomColumns(currentSettings.widget_placement?.custom_columns || DEFAULT_CUSTOM_COLUMNS);
      setErrors([]);
      setDraggedWidget(null);
      setDragSource(null);
      setDropTarget(null);
      const spans = {}, dirs = {}, collapsed = {}, rowSpans = {};
      Object.entries(currentSettings.widget_settings || {}).forEach(([id, s]) => {
        if (s.colSpan && s.colSpan > 1) spans[id] = s.colSpan;
        if (s.spanDirection) dirs[id] = s.spanDirection;
        if (s.defaultCollapsed) collapsed[id] = true;
        if (s.rowSpan && s.rowSpan > 1) rowSpans[id] = s.rowSpan;
      });
      setWidgetSpans(spans);
      setSpanDirections(dirs);
      setWidgetCollapsed(collapsed);
      setWidgetRowSpans(rowSpans);
    }
  }, [isOpen, embedded, currentSettings]);

  // Expose save and reset to parent when in embedded mode
  useImperativeHandle(ref, () => ({
    save: handleSave,
    reset: handleReset
  }));

  const getCurrentLayout = () => {
    if (layoutPreset === 'custom') {
      return {
        id: 'custom', name: 'Custom', columns: customColumns,
        gridTemplate: customColumns.map(c => c.width).join(' ')
      };
    }
    return layoutPresets[layoutPreset];
  };

  const currentLayout = getCurrentLayout();
  const numColumns = currentLayout.columns.length;

  // === Layout change handlers ===
  const handleLayoutChange = (newLayout) => {
    if (newLayout === 'custom') {
      setLayoutPreset('custom');
      const numCols = customColumns.length;
      const newPlacement = { custom_columns: customColumns };
      for (let i = 0; i < numCols; i++) newPlacement[`column_${i}`] = [];
      const allWidgets = [];
      Object.entries(widgetPlacement).forEach(([key, widgets]) => {
        if (key.startsWith('column_') && Array.isArray(widgets)) allWidgets.push(...widgets);
      });
      allWidgets.forEach((widgetId, idx) => {
        const colIdx = idx % numCols;
        if (canPlaceWidget(widgetId, customColumns[colIdx]?.type)) {
          newPlacement[`column_${colIdx}`].push(widgetId);
        }
      });
      setWidgetPlacement(newPlacement);
      setErrors([]);
      return;
    }
    setLayoutPreset(newLayout);
    const newLayoutConfig = layoutPresets[newLayout];
    const newNumCols = newLayoutConfig.columns.length;
    const currentColCount = Object.keys(widgetPlacement).filter(k => k.startsWith('column_')).length;
    if (newNumCols !== currentColCount) {
      setWidgetPlacement(defaultPlacements[newLayout] || {});
    } else {
      const newPlacement = {};
      let hasErrors = false;
      Object.entries(widgetPlacement).forEach(([columnKey, widgets]) => {
        if (!columnKey.startsWith('column_')) return;
        const ci = parseInt(columnKey.replace('column_', ''));
        const ct = newLayoutConfig.columns[ci]?.type;
        newPlacement[columnKey] = (widgets || []).filter(wid => {
          const ok = canPlaceWidget(wid, ct);
          if (!ok) hasErrors = true;
          return ok;
        });
      });
      setWidgetPlacement(hasErrors ? (defaultPlacements[newLayout] || {}) : newPlacement);
    }
    setErrors([]);
  };

  const handleCustomColumnCount = (count) => {
    const newCols = [];
    for (let i = 0; i < count; i++) newCols.push(customColumns[i] || { type: 'medium', width: '1fr' });
    setCustomColumns(newCols);
    const newPlacement = { custom_columns: newCols };
    for (let i = 0; i < count; i++) newPlacement[`column_${i}`] = widgetPlacement[`column_${i}`] || [];
    const removed = [];
    Object.entries(widgetPlacement).forEach(([key, widgets]) => {
      if (!key.startsWith('column_')) return;
      const idx = parseInt(key.replace('column_', ''));
      if (idx >= count && Array.isArray(widgets)) removed.push(...widgets);
    });
    if (removed.length > 0) {
      newPlacement[`column_${count - 1}`] = [...(newPlacement[`column_${count - 1}`] || []), ...removed];
    }
    setWidgetPlacement(newPlacement);
  };

  const handleCustomColumnType = (colIndex, widthOption) => {
    const newCols = [...customColumns];
    newCols[colIndex] = { type: widthOption.type, width: widthOption.value };
    setCustomColumns(newCols);
    setWidgetPlacement(prev => ({ ...prev, custom_columns: newCols }));
    const colKey = `column_${colIndex}`;
    const colWidgets = widgetPlacement[colKey] || [];
    const invalid = colWidgets.filter(id => !canPlaceWidget(id, widthOption.type));
    if (invalid.length > 0) {
      setWidgetPlacement(prev => ({
        ...prev, custom_columns: newCols,
        [colKey]: colWidgets.filter(id => canPlaceWidget(id, widthOption.type))
      }));
      setErrors(invalid.map(id => `"${widgetRegistry[id]?.name}" removed (incompatible with ${widthOption.type})`));
    }
  };

  // === Span / collapsed handlers ===
  const handleSpanChange = (widgetId, span) => {
    if (span <= 1) {
      setWidgetSpans(prev => { const n = { ...prev }; delete n[widgetId]; return n; });
      setSpanDirections(prev => { const n = { ...prev }; delete n[widgetId]; return n; });
    } else {
      setWidgetSpans(prev => ({ ...prev, [widgetId]: span }));
    }
  };

  const handleDirectionChange = (widgetId, direction) => {
    setSpanDirections(prev => {
      if (direction === 'right') { const n = { ...prev }; delete n[widgetId]; return n; }
      return { ...prev, [widgetId]: direction };
    });
  };

  const handleRowSpanChange = (widgetId, span) => {
    if (span <= 1) {
      setWidgetRowSpans(prev => { const n = { ...prev }; delete n[widgetId]; return n; });
    } else {
      setWidgetRowSpans(prev => ({ ...prev, [widgetId]: span }));
    }
  };

  const handleCollapsedToggle = (widgetId) => {
    setWidgetCollapsed(prev => {
      const n = { ...prev };
      if (n[widgetId]) delete n[widgetId]; else n[widgetId] = true;
      return n;
    });
  };

  // === Drag and drop ===
  const handleDragStart = (e, widgetId, sourceColumn) => {
    setDraggedWidget(widgetId);
    setDragSource(sourceColumn);
    setErrors([]);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', widgetId);
    setTimeout(() => e.target.classList.add('dragging'), 0);
  };

  const handleDragEnd = () => {
    document.querySelectorAll('.dragging').forEach(el => el.classList.remove('dragging'));
    setDraggedWidget(null);
    setDragSource(null);
    setDropTarget(null);
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    const widgetId = draggedWidget || e.dataTransfer.types.includes('text/plain');
    if (!widgetId) return;
    if (draggedWidget) {
      const ci = parseInt(columnKey.replace('column_', ''));
      const ct = currentLayout.columns[ci]?.type;
      if (canPlaceWidget(draggedWidget, ct)) {
        e.dataTransfer.dropEffect = 'move';
        setDropTarget(columnKey);
      } else {
        e.dataTransfer.dropEffect = 'none';
      }
    }
  };

  const handleDragLeave = (e) => {
    const related = e.relatedTarget;
    if (!related || !e.currentTarget.contains(related)) setDropTarget(null);
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    const widgetId = draggedWidget || e.dataTransfer.getData('text/plain');
    if (!widgetId) return;
    const ci = parseInt(targetColumn.replace('column_', ''));
    const ct = currentLayout.columns[ci]?.type;
    if (!canPlaceWidget(widgetId, ct)) {
      setErrors([`"${widgetRegistry[widgetId]?.name}" cannot be placed in ${ct} columns`]);
      setDropTarget(null);
      return;
    }
    setWidgetPlacement(prev => {
      const np = { ...prev };
      // Remove from source
      Object.keys(np).forEach(key => {
        if (key.startsWith('column_') && Array.isArray(np[key])) {
          np[key] = np[key].filter(id => id !== widgetId);
        }
      });
      const tw = [...(np[targetColumn] || [])];
      tw.push(widgetId);
      np[targetColumn] = tw;
      return np;
    });
    setErrors([]);
    setDropTarget(null);
  };

  const handleRemoveWidget = (widgetId, columnKey) => {
    setWidgetPlacement(prev => ({
      ...prev, [columnKey]: (prev[columnKey] || []).filter(id => id !== widgetId)
    }));
    handleSpanChange(widgetId, 1);
    handleRowSpanChange(widgetId, 1);
  };

  const handleReorderWidget = (widgetId, columnKey, direction) => {
    setWidgetPlacement(prev => {
      const widgets = [...(prev[columnKey] || [])];
      const ci = widgets.indexOf(widgetId);
      if (ci === -1) return prev;
      const ni = direction === 'up' ? ci - 1 : ci + 1;
      if (ni < 0 || ni >= widgets.length) return prev;
      [widgets[ci], widgets[ni]] = [widgets[ni], widgets[ci]];
      return { ...prev, [columnKey]: widgets };
    });
  };

  // === Save / Reset ===
  const handleSave = async () => {
    const ms = { ...(currentSettings.widget_settings || {}) };
    Object.entries(widgetSpans).forEach(([id, span]) => {
      ms[id] = { ...(ms[id] || {}), colSpan: span };
      if (spanDirections[id]) ms[id].spanDirection = spanDirections[id];
    });
    Object.keys(ms).forEach(id => {
      if (!widgetSpans[id]) {
        const { colSpan, spanDirection, ...rest } = ms[id] || {};
        ms[id] = rest;
      } else if (!spanDirections[id] && ms[id]?.spanDirection) {
        const { spanDirection, ...rest } = ms[id];
        ms[id] = rest;
      }
    });
    Object.entries(widgetRowSpans).forEach(([id, span]) => {
      ms[id] = { ...(ms[id] || {}), rowSpan: span };
    });
    Object.keys(ms).forEach(id => {
      if (!widgetRowSpans[id] && ms[id]?.rowSpan) {
        const { rowSpan, ...rest } = ms[id];
        ms[id] = rest;
      }
    });
    Object.keys(widgetRegistry).forEach(id => {
      if (widgetCollapsed[id]) {
        ms[id] = { ...(ms[id] || {}), defaultCollapsed: true };
      } else if (ms[id]?.defaultCollapsed) {
        const { defaultCollapsed, ...rest } = ms[id];
        ms[id] = rest;
      }
    });
    const placement = { ...widgetPlacement };
    if (layoutPreset === 'custom') placement.custom_columns = customColumns;
    else delete placement.custom_columns;
    const result = await onSave({ layout_preset: layoutPreset, widget_placement: placement, widget_settings: ms });
    if (result.success && !embedded && onClose) onClose();
    else if (!result.success) setErrors([result.error || 'Failed to save settings']);
  };

  const handleReset = () => {
    setShowResetConfirm(true);
  };

  const confirmReset = async () => {
    setShowResetConfirm(false);
    await onReset();
  };

  const getUnplacedWidgets = () => {
    const placed = new Set();
    Object.entries(widgetPlacement).forEach(([key, widgets]) => {
      if (key.startsWith('column_') && Array.isArray(widgets)) widgets.forEach(id => placed.add(id));
    });
    const unplaced = Object.keys(widgetRegistry).filter(id => !placed.has(id));
    // Only show up to 3 unplaced spacers at a time, and hide widgets this user
    // is not entitled to entirely (they shouldn't even know these exist) —
    // adminOnly for the worker logs, badges for the rep widgets.
    let spacerCount = 0;
    return unplaced.filter(id => {
      const widget = widgetRegistry[id];
      if (!isWidgetVisibleTo(widget, viewer)) return false;
      if (widget?.isSpacer) {
        spacerCount++;
        return spacerCount <= 3;
      }
      return true;
    });
  };

  const unplacedWidgets = getUnplacedWidgets();

  const getMaxSpan = (columnIndex, direction) => {
    return direction === 'left' ? columnIndex + 1 : numColumns - columnIndex;
  };

  // === Build grid items for the visual preview ===
  // Zip widgets from all columns row-by-row, respecting spans
  const buildGridItems = () => {
    const colLists = Array.from({ length: numColumns }, (_, i) =>
      [...(widgetPlacement[`column_${i}`] || [])]
    );
    const maxLen = Math.max(...colLists.map(l => l.length), 0);
    const items = [];
    const colPointers = Array(numColumns).fill(0);
    // Track which columns are blocked (by a spanning widget in another column)
    const blockedUntil = Array(numColumns).fill(0); // row index until which this col is blocked

    for (let row = 0; row < maxLen + numColumns; row++) {
      let anyOutput = false;
      for (let col = 0; col < numColumns; col++) {
        if (blockedUntil[col] > row) continue; // skip blocked
        const idx = colPointers[col];
        if (idx >= colLists[col].length) continue;

        const widgetId = colLists[col][idx];
        const span = widgetSpans[widgetId] || 1;
        const dir = spanDirections[widgetId] || 'right';
        const startCol = dir === 'left' ? Math.max(0, col - (span - 1)) : col;
        const clampedSpan = Math.min(span, numColumns - startCol);

        // Check if all columns this would span are free
        let canPlace = true;
        for (let c = startCol; c < startCol + clampedSpan; c++) {
          if (c !== col && blockedUntil[c] > row) { canPlace = false; break; }
        }
        if (!canPlace) continue;

        items.push({
          widgetId, homeCol: col, gridColumn: startCol + 1,
          gridColumnSpan: clampedSpan, row: row + 2 // row 1 is headers
        });
        colPointers[col]++;
        anyOutput = true;

        // Block covered columns for next row
        if (clampedSpan > 1) {
          for (let c = startCol; c < startCol + clampedSpan; c++) {
            if (c !== col) blockedUntil[c] = row + 1;
          }
        }
      }
      if (!anyOutput && colPointers.every((p, i) => p >= colLists[i].length)) break;
    }
    return items;
  };

  const gridItems = buildGridItems();

  // === Render widget item (compact, two-line for spannable) ===
  const renderWidgetItem = ({ widgetId, homeCol, gridColumn, gridColumnSpan, row }) => {
    const widget = widgetRegistry[widgetId];
    if (!widget) return null;
    const colKey = `column_${homeCol}`;
    const colWidgets = widgetPlacement[colKey] || [];
    const widgetIndex = colWidgets.indexOf(widgetId);
    const currentSpan = widgetSpans[widgetId] || 1;
    const currentDir = spanDirections[widgetId] || 'right';
    const maxRight = getMaxSpan(homeCol, 'right');
    const maxLeft = getMaxSpan(homeCol, 'left');
    const canSpanL = homeCol > 0;
    const canSpanR = homeCol < numColumns - 1;
    const maxForDir = currentDir === 'left' ? maxLeft : maxRight;
    const isCollapsed = widgetCollapsed[widgetId] || false;
    const currentRowSpan = widgetRowSpans[widgetId] || 1;
    const maxRows = Math.max(...Array.from({ length: numColumns }, (_, i) =>
      (widgetPlacement[`column_${i}`] || []).length
    ), 1);

    return (
      <div
        key={widgetId}
        className={`grid-widget-item ${draggedWidget === widgetId ? 'dragging' : ''} ${currentSpan > 1 ? 'spanning' : ''}`}
        style={{
          gridColumn: gridColumnSpan > 1 ? `${gridColumn} / span ${gridColumnSpan}` : gridColumn,
          gridRow: row
        }}
        draggable
        onDragStart={(e) => handleDragStart(e, widgetId, colKey)}
        onDragEnd={handleDragEnd}
      >
        {/* Row 1: identity + core actions */}
        <div className="gwi-row1">
          <span className="drag-handle material-symbols-rounded">drag_indicator</span>
          <span className="material-symbols-rounded widget-icon">{widget.icon}</span>
          <span className="widget-name">{widget.name}</span>
          {currentSpan > 1 && (
            <span className="span-badge">{currentSpan}col {currentDir === 'left' ? '\u2190' : '\u2192'}</span>
          )}
          <div className="gwi-actions-core">
            <button
              className={`btn-icon btn-sm btn-collapse-toggle ${isCollapsed ? 'active' : ''}`}
              onClick={() => handleCollapsedToggle(widgetId)}
              title={isCollapsed ? 'Starts collapsed' : 'Starts expanded'}
            >
              <span className="material-symbols-rounded">{isCollapsed ? 'visibility_off' : 'visibility'}</span>
            </button>
            <button className="btn-icon btn-sm" onClick={() => handleReorderWidget(widgetId, colKey, 'up')}
              disabled={widgetIndex <= 0} title="Move up">
              <span className="material-symbols-rounded">arrow_upward</span>
            </button>
            <button className="btn-icon btn-sm" onClick={() => handleReorderWidget(widgetId, colKey, 'down')}
              disabled={widgetIndex >= colWidgets.length - 1} title="Move down">
              <span className="material-symbols-rounded">arrow_downward</span>
            </button>
            <button className="btn-icon btn-sm btn-remove" onClick={() => handleRemoveWidget(widgetId, colKey)}
              title="Remove">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>
        {/* Row 2: col span + row span controls */}
        {widget.canSpan && ((canSpanL || canSpanR) || maxRows > 1) && (
          <div className="gwi-row2">
            {(canSpanL || canSpanR) && (<>
              <span className="gwi-span-label">Cols:</span>
              <select className="span-select" value={currentSpan}
                onChange={(e) => handleSpanChange(widgetId, parseInt(e.target.value))}
                onClick={(e) => e.stopPropagation()}>
                {Array.from({ length: maxForDir }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n === 1 ? '1' : n}</option>
                ))}
              </select>
              {currentSpan > 1 && (
                <div className="span-direction">
                  <button className={`btn-icon btn-sm btn-dir ${currentDir === 'left' ? 'active' : ''}`}
                    onClick={() => { handleDirectionChange(widgetId, 'left'); if (currentSpan > maxLeft) handleSpanChange(widgetId, maxLeft); }}
                    disabled={!canSpanL} title="Span left">
                    <span className="material-symbols-rounded">west</span>
                  </button>
                  <button className={`btn-icon btn-sm btn-dir ${currentDir === 'right' ? 'active' : ''}`}
                    onClick={() => { handleDirectionChange(widgetId, 'right'); if (currentSpan > maxRight) handleSpanChange(widgetId, maxRight); }}
                    disabled={!canSpanR} title="Span right">
                    <span className="material-symbols-rounded">east</span>
                  </button>
                </div>
              )}
              {maxRows > 1 && <span className="gwi-span-divider" />}
            </>)}
            {maxRows > 1 && (<>
              <span className="gwi-span-label">Rows:</span>
              <select className="span-select" value={currentRowSpan}
                onChange={(e) => handleRowSpanChange(widgetId, parseInt(e.target.value))}
                onClick={(e) => e.stopPropagation()}>
                {Array.from({ length: maxRows }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n === 1 ? '1' : n}</option>
                ))}
              </select>
            </>)}
          </div>
        )}
      </div>
    );
  };

  // === Shared content renderer (used by both embedded and modal modes) ===
  const renderContent = () => (
    <>
      <div className="configurator-section">
        <h4>Choose Layout</h4>
          <div className="layout-presets">
            {Object.entries(layoutPresets).map(([key, preset]) => (
              <button key={key} className={`layout-preset-btn ${layoutPreset === key ? 'active' : ''}`}
                onClick={() => handleLayoutChange(key)}>
                <div className="layout-preview">
                  {preset.columns.map((col, i) => (
                    <div key={i} className={`preview-column preview-${col.type}`} />
                  ))}
                </div>
                <span className="layout-name">{preset.name}</span>
              </button>
            ))}
            <button className={`layout-preset-btn ${layoutPreset === 'custom' ? 'active' : ''}`}
              onClick={() => handleLayoutChange('custom')}>
              <div className="layout-preview">
                <div className="preview-column preview-custom">
                  <span className="material-symbols-rounded" style={{ fontSize: 20, opacity: 0.6 }}>tune</span>
                </div>
              </div>
              <span className="layout-name">Custom</span>
            </button>
          </div>
        </div>

        {/* Custom Layout Builder */}
        {layoutPreset === 'custom' && (
          <div className="configurator-section custom-layout-section">
            <h4>Custom Layout</h4>
            <div className="custom-layout-builder">
              <div className="custom-column-count">
                <span className="custom-label">Columns:</span>
                {[2, 3, 4].map(count => (
                  <button key={count} className={`custom-count-btn ${customColumns.length === count ? 'active' : ''}`}
                    onClick={() => handleCustomColumnCount(count)}>{count}</button>
                ))}
              </div>
              <div className="custom-columns-config">
                {customColumns.map((col, idx) => (
                  <div key={idx} className="custom-column-item">
                    <span className="custom-col-label">Col {idx + 1}</span>
                    <div className="custom-width-options">
                      {columnWidthOptions.map(opt => (
                        <button key={opt.value}
                          className={`custom-width-btn ${col.width === opt.value ? 'active' : ''}`}
                          onClick={() => handleCustomColumnType(idx, opt)}
                          title={`${opt.label} (${opt.value})`}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="custom-preview">
                <div className="layout-preview" style={{ height: 40 }}>
                  {customColumns.map((col, i) => (
                    <div key={i} className={`preview-column preview-${col.type}`}
                      style={{ flex: col.type === 'wide' ? 2 : col.type === 'medium' ? 1.5 : 1 }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Widget Assignment - Grid Preview */}
        <div className="configurator-section">
          <h4>Assign Widgets to Columns</h4>
          <p className="section-hint">
            Drag widgets between columns. Use span controls to stretch across columns. Use arrows to reorder vertically.
          </p>

          <div className="assignment-grid" style={{
            gridTemplateColumns: `repeat(${numColumns}, 1fr)`
          }}>
            {/* Column headers + drop zones */}
            {currentLayout.columns.map((column, index) => {
              const columnKey = `column_${index}`;
              const isTarget = dropTarget === columnKey;
              const canDrop = draggedWidget && canPlaceWidget(draggedWidget, column.type);
              return (
                <div key={`hdr-${index}`}
                  className={`grid-column-header ${isTarget ? 'drop-target' : ''} ${draggedWidget && !canDrop ? 'drop-invalid' : ''}`}
                  onDragOver={(e) => handleDragOver(e, columnKey)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, columnKey)}>
                  <span className="column-type">{column.type}</span>
                  <span className="column-label">Col {index + 1}</span>
                </div>
              );
            })}

            {/* Widget items placed in grid */}
            {gridItems.map(item => renderWidgetItem(item))}

            {/* Drop zones (always rendered for stable drag targets) */}
            {currentLayout.columns.map((column, index) => {
              const columnKey = `column_${index}`;
              const colWidgets = widgetPlacement[columnKey] || [];
              const isTarget = dropTarget === columnKey;
              const canDrop = draggedWidget && canPlaceWidget(draggedWidget, column.type);
              const isEmpty = colWidgets.length === 0;
              return (
                <div key={`drop-${index}`}
                  className={`grid-drop-zone ${isTarget ? 'drop-target' : ''} ${draggedWidget && !canDrop ? 'drop-invalid' : ''} ${!isEmpty && !draggedWidget ? 'hidden' : ''}`}
                  style={{ gridColumn: index + 1 }}
                  onDragOver={(e) => handleDragOver(e, columnKey)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, columnKey)}>
                  {isEmpty && !draggedWidget && (
                    <>
                      <span className="material-symbols-rounded" style={{ fontSize: 24, opacity: 0.4 }}>widgets</span>
                      <span>Drop here</span>
                    </>
                  )}
                  {draggedWidget && canDrop && <span>Drop here</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Unused Widgets */}
        {unplacedWidgets.length > 0 && (
          <div className="configurator-section">
            <h4>Available Widgets</h4>
            <p className="section-hint">Drag into a column above to add.</p>
            <div className="unused-widgets-grid">
              {unplacedWidgets.map(widgetId => {
                const widget = widgetRegistry[widgetId];
                return (
                  <div key={widgetId}
                    className={`unused-widget-card ${draggedWidget === widgetId ? 'dragging' : ''}`}
                    draggable onDragStart={(e) => handleDragStart(e, widgetId, 'unplaced')}
                    onDragEnd={handleDragEnd}>
                    <div className="unused-widget-preview">
                      <span className="material-symbols-rounded">{widget.icon}</span>
                    </div>
                    <div className="unused-widget-info">
                      <span className="unused-widget-name">{widget.name}</span>
                      <span className="unused-widget-restriction">{widget.allowedColumns.join(', ')}</span>
                    </div>
                    <span className="drag-hint material-symbols-rounded">drag_indicator</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {errors.length > 0 && (
          <div className="configurator-errors" style={{ pointerEvents: 'none' }}>
            {errors.map((error, i) => (
              <div key={i} className="error-message">
                <span className="material-symbols-rounded">error</span>{error}
              </div>
            ))}
          </div>
        )}

        <ConfirmDialog
          isOpen={showResetConfirm}
          title="Reset Dashboard"
          message="Reset dashboard to default layout? This cannot be undone."
          type="warning"
          confirmText="Reset"
          onConfirm={confirmReset}
          onCancel={() => setShowResetConfirm(false)}
        />
    </>
  );

  // In embedded mode, render just the content without Modal wrapper
  if (embedded) {
    return (
      <div className="configurator-content configurator-embedded">
        {renderContent()}
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configure Dashboard" size="xl"
      className="dashboard-configurator-modal">
      <div className="configurator-content">
        {renderContent()}
      </div>

      <div className="modal-footer">
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout btn-danger" onClick={handleReset} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">undo</span></div>
              <span className="btn-label">Reset to Default</span>
            </button>
          </div>
        </div>
        <div className="footer-right">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={handleSave} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
});

export default DashboardConfigurator;
